import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from "@/components/flexkit/Command";
import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { useProject } from "@/queries/project-queries";
import { type PlainAgentExecution, useGroupedAgentExecutions } from "@/queries/agent-queries";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useEnvironmentsByProjects, type EnvironmentsGroup } from "@/hooks/use-environments-by-projects";
import { getProjectDisplayName } from "@/utils/environment";
import { mostRecentAgentExecution } from "@/utils/agent-executions";
import { gitStatusChangesCount, formatChangesCount } from "@/utils/git-status";
import { ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { memo, useCallback, useMemo, type FC } from "react";

type AgentExecutionsByEnvironment = Map<string, PlainAgentExecution[]>;

import type { NavigateFunction } from "react-router-dom";

type EnvironmentsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

export const EnvironmentsPage: FC<EnvironmentsPageProps> = memo(function EnvironmentsPage({ runCommand, navigate }) {
    const { data: groups, isLoading: isLoadingEnvs } = useEnvironmentsByProjects({
        includeAgentCreatedEnvironments: true,
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ACTIVE,
    });
    const { data: groupedAgentExecutions, isPending: isLoadingExecutions } = useGroupedAgentExecutions();

    if (isLoadingEnvs || isLoadingExecutions) {
        return <CommandLoading>Loading environments...</CommandLoading>;
    }

    const agentExecutionsByEnvironment = groupedAgentExecutions?.environmentScoped ?? new Map();

    if (groups.length === 0) {
        return <CommandEmpty>No environments found.</CommandEmpty>;
    }

    return (
        <>
            {groups.map((group) => (
                <ProjectGroup
                    key={group.projectId || group.repoFullName || "scratch"}
                    group={group}
                    agentExecutionsByEnvironment={agentExecutionsByEnvironment}
                    runCommand={runCommand}
                    navigate={navigate}
                />
            ))}
        </>
    );
});

const ProjectGroup: FC<{
    group: EnvironmentsGroup;
    agentExecutionsByEnvironment: AgentExecutionsByEnvironment;
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ group, agentExecutionsByEnvironment, runCommand, navigate }) => {
    const { data: project } = useProject(group.projectId);
    const heading = group.projectId ? getProjectDisplayName(project) : group.repoFullName || "From scratch";

    return (
        <CommandGroup heading={heading}>
            {group.environments.map((env) => (
                <EnvironmentItem
                    key={env.id}
                    environment={env}
                    agentExecutions={agentExecutionsByEnvironment.get(env.id) ?? []}
                    runCommand={runCommand}
                    navigate={navigate}
                />
            ))}
        </CommandGroup>
    );
};

const EnvironmentItem: FC<{
    environment: PlainEnvironment;
    agentExecutions: PlainAgentExecution[];
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ environment, agentExecutions, runCommand, navigate }) => {
    const agentExecution = useMemo(() => mostRecentAgentExecution(agentExecutions), [agentExecutions]);
    const name = useEnvironmentName(environment, agentExecution);
    const git = environment.status?.content?.git;
    const changesCount = gitStatusChangesCount(git);
    const hasChanges = changesCount !== null && changesCount > 0;
    const handleSelect = useCallback(
        () => runCommand(() => navigate(`/details/${environment.id}`)),
        [runCommand, navigate, environment.id],
    );

    return (
        <CommandItem value={`${environment.id} ${name}`} onSelect={handleSelect}>
            <span className="flex size-4 shrink-0 items-center justify-center">
                <EnvironmentStatusDot env={environment} size="sm" />
            </span>
            <span className="truncate">{name}</span>
            {hasChanges && (
                <span className="ml-auto inline-flex h-4 items-center rounded-full bg-surface-brand px-1.5 font-mono text-[10px] font-medium leading-none text-content-brand dark:bg-content-brand/25 dark:text-surface-brand/80">
                    {formatChangesCount(changesCount)}
                </span>
            )}
        </CommandItem>
    );
};
